<template>
  <div class="fill-height d-flex flex-column" data-locator="signing-checklist-add-page">
    <div v-show="!creationLoading" class="flex-grow-1 d-flex flex-column">
      <v-stepper
        v-model="stepActive"
        alt-labels
        :class="`mx-auto w-100 flex-grow-1 overflow-y-auto ${currentStepActiveClass}`"
        :non-linear="hasPassedStep3"
        flat
        :rounded="false"
      >
        <v-stepper-header
          v-show="!hasOnlyReadOnlyFiles"
          class="mx-auto"
          style="max-width: 600px"
        >
          <template v-for="i in isStep3Disabled ? 2 : 3" :key="'step' + i">
            <v-divider v-if="i !== 1" :key="i" class="mt-6"></v-divider>
            <v-stepper-item
              :value="i"
              :step="i"
              :editable="hasPassedStep3"
              :complete="hasPassedStep3 && i < stepActive"
              edit-icon="$complete"
              color="tertiary"
              class="pa-3"
            >
              {{ $t(`project.views.SigningChecklistAdd.step${i}Title`) }}
            </v-stepper-item>
          </template>
        </v-stepper-header>
        <v-stepper-window>
          <v-progress-circular
            v-show="creationLoading"
            style="width: 64px; height: 64px; margin-inline: auto;"
            color="primary"
            indeterminate
            size="64"
          />

          <v-stepper-window-item :value="1">
            <SigningChecklistAddStep1
              v-if="stepActive === 1 && !creationLoading"
              :mnemo="mnemo"
              :cannotAdd="cannotAdd"
              @updateUploadedFiles="updateUploadedFiles"
            />
          </v-stepper-window-item>
          <v-stepper-window-item :value="2">
            <SigningChecklistAddStep2 v-if="stepActive === 2" :imported-files="importedFiles" />
          </v-stepper-window-item>
          <v-stepper-window-item v-if="!isStep3Disabled" :value="3">
            <SigningChecklistAddStep3 v-if="stepActive === 3" :envelopeToPrepareIndex="envelopeToPrepareIndex" />
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>

      <v-row
        v-if="!hasOnlyReadOnlyFiles"
        class="stepper__actionsBar SigningChecklistAdd-actionsBar"
        no-gutters
        :class="{
              'justify-space-between': stepActive > 1,
              'justify-end': stepActive === 1,
             }"
      >
        <v-col class="d-flex column-gap-2">
          <AppButton
            v-if="stepActive === 2"
            color="white"
            data-locator="previous-button"
            @click="stepActive--"
          >
            <app-icon
              v-if="$vuetify.display.mdAndUp"
              icon-name="arrow-left-long"
              icon-weight="fal"
              size="small"
              class="mr-2"
            />
            {{ $t(`common.previous`) }}
          </AppButton>
          <AppButton
            v-if="envelopeToPrepareIndex === 0 && stepActive === 3"
            color="white"
            data-locator="previous-button"
            @click="stepActive--"
          >
            <app-icon
              v-if="$vuetify.display.mdAndUp"
              icon-name="arrow-left-long"
              icon-weight="fal"
              size="small"
              class="mr-2"
            />
            {{ $t(`common.previous`) }}
          </AppButton>
          <AppButton
            v-if="envelopeToPrepareIndex > 0 && stepActive === 3"
            color="white"
            data-locator="previous-document-button"
            @click="envelopeToPrepareIndex--"
          >
            <app-icon
              v-if="$vuetify.display.mdAndUp"
              icon-name="arrow-left-long"
              icon-weight="fal"
              size="small"
              class="mr-2"
            />
            {{ $t('project.views.SigningChecklistAdd.previousDocument') }}
          </AppButton>
          <AppButton
            v-if="stepActive < 3"
            color="white"
            data-locator="cancel-button"
            @click="onCancel"
          >
            {{ $t(`common.cancel`) }}
          </AppButton>
        </v-col>
        <v-col class="text-center">
          <div v-if="stepActive > 1" class="text-caption">
            <p v-if="lastSave">{{ $t('project.views.SigningChecklistAdd.autoSave', { hour: dayjs(lastSave).format('HH:mm:ss') }) }}</p>
            <p>{{ $t('project.views.SigningChecklistAdd.leaveInfo') }}</p>
          </div>
        </v-col>
        <v-col class="d-flex justify-end column-gap-2">
          <AppButton
            v-if="stepActive === 2"
            color="white"
            :loading="actionLoading"
            data-locator="save-and-exit-button"
            @click="onSaveAndExit"
          >
            {{ $t(`common.saveAndExit`) }}
          </AppButton>
          <AppButton
            v-if="stepActive !== 3 && !(stepActive === 2 && isStep3Disabled)"
            color="primary"
            :disabled="!anyImportedFiles || (filesToUpload && !allFilesToUploadSuccess)"
            :loading="actionLoading"
            data-locator="next-button"
            @click="checkStepValidation"
          >
            {{ $t(`common.next`) }}
            <app-icon
              v-if="stepActive < 3 && $vuetify.display.mdAndUp"
              icon-name="arrow-right-long"
              icon-weight="fal"
              size="small"
              class="ml-2"
            />
          </AppButton>
          <AppButton
            v-if="stepActive === 2 && isStep3Disabled"
            color="primary"
            :disabled="!anyImportedFiles || (filesToUpload && !allFilesToUploadSuccess)"
            :loading="actionLoading"
            data-locator="validate-button"
            @click="checkStepValidation"
          >
            {{ $t(`common.validate`) }}
          </AppButton>
          <template v-if="stepActive === 3">
            <AppButton color="white" data-locator="save-and-exit-button" @click="onSaveAndExit">
              {{ $t(`common.saveAndExit`) }}
            </AppButton>
            <AppButton
              v-if="envelopeToPrepareIndex === draftedDocuments.length - 1"
              color="primary"
              data-locator="finish-button"
              @click="onFinish"
            >
              {{ $t('common.finish') }}
            </AppButton>
            <AppButton
              v-if="envelopeToPrepareIndex < draftedDocuments.length - 1"
              color="white"
              data-locator="next-document-button"
              @click="envelopeToPrepareIndex++"
            >
              {{ $t('project.views.SigningChecklistAdd.nextDocument') }}
              <app-icon
                v-if="$vuetify.display.mdAndUp"
                icon-name="arrow-right-long"
                icon-weight="fal"
                size="small"
                class="ml-2"
              />
            </AppButton>
          </template>
        </v-col>
      </v-row>
      <v-row
        v-else
        class="stepper__actionsBar SigningChecklistAdd-actionsBar justify-end"
        no-gutters
      >
        <AppButton
          color="white"
          class="mr-2"
          data-locator="save-and-exit-button"
          @click="onSaveAndExit"
        >
          {{ $t(`common.saveAndExit`) }}
        </AppButton>
        <AppButton color="primary" data-locator="back-to-signing-checklist-button" @click="onFinish">
          {{ $t('project.views.SigningChecklistAdd.backBtnLabel')}}
        </AppButton>
      </v-row>
    </div>

    <SigningChecklistAddStep2RecipientWarningDialog
      v-if="showStep2RecipientWarningDialog"
      @confirm="confirmWarningStep2Dialog"
      @close="showStep2RecipientWarningDialog = false"
    />
    <SigningChecklistAddStep2SignatoryWarningDialog
      v-if="showStep2SignatoryWarningDialog"
      :files="importedFiles"
      @confirm="confirmWarningStep2Dialog"
      @close="showStep2SignatoryWarningDialog = false"
    />
    <SigningChecklistAddStep2MissingRecipientWarningDialog
      v-if="showStep2MissingRecipientWarningDialog"
      :imported-files="importedFiles"
      @close="showStep2MissingRecipientWarningDialog = false"
    />
    <SigningChecklistAddBeforeLeaveDialog
      v-if="leaveLocation && showBeforeLeaveDialog"
      @confirm="upsertAllImportedFilesToSteps"
      @close="$router.push(leaveLocation)"
    />
    <SigningChecklistPrepareCancelDialog
      v-if="showCancelDialog"
      @confirm="swapFiles ? resetSwapFiles() :returnToSigningChecklist()"
      @close="showCancelDialog = false"
    />
    <SigningChecklistTagsOverlapsDialog
      v-if="showTagsOverlapsDialog"
      :documents="importedFilesInDraft"
      :tags-overlaps="tagsOverlaps"
      @confirm="returnToSigningChecklist"
      @close="showTagsOverlapsDialog = false"
    />
    <SwapFilesDialog
      v-if="swapFiles"
      :mnemo="mnemo"
      @cancel-swap="showCancelDialog = true"
      @close="resetSwapFiles"
      @confirm="onSwapFileConfirm"
    />
  </div>
</template>

<script lang="ts">
import { useVuelidate } from '@vuelidate/core'
import dayjs from 'dayjs'
import { cloneDeep } from 'lodash-es'
import { defineComponent,  PropType, provide } from 'vue'
import { NavigationGuardNext, RouteLocationNamedRaw, RouteLocationNormalized, useRoute } from 'vue-router'
import { createNamespacedHelpers, mapActions, mapGetters, mapState } from 'vuex'

import AppButton from '@/common/buttons/AppButton.vue'
import { track } from '@/common/pendo/agent'
import { canShowReadOnlyStep ,
  formatImportedFile,
  formatStep,
  formatStepFromEnvelopeTemplate,
} from '@/common/utils/signingChecklist'
import { toast } from '@/common/utils/toast'
import SigningChecklistAddStep1 from '@/project/signing-checklist/add/step1/SigningChecklistAddStep1.vue'
import SigningChecklistAddStep2 from '@/project/signing-checklist/add/step2/SigningChecklistAddStep2.vue'
import SigningChecklistAddStep2MissingRecipientWarningDialog from '@/project/signing-checklist/add/step2/SigningChecklistAddStep2MissingRecipientWarningDialog.vue'
import { ENVELOPE_STATUS, ENVELOPE_TYPES, SIGNATURE_TYPE_HANDWRITTEN, SIGNATURE_TYPES } from '@/project/signing-checklist/constants'
import SigningChecklistAddBeforeLeaveDialog from '@/project/signing-checklist/dialogs/SigningChecklistAddBeforeLeaveDialog.vue'
import SigningChecklistPrepareCancelDialog from '@/project/signing-checklist/dialogs/SigningChecklistPrepareCancelDialog.vue'
import SigningChecklistTagsOverlapsDialog from '@/project/signing-checklist/dialogs/SigningChecklistTagsOverlapsDialog.vue'
import SwapFilesDialog from '@/project/signing-checklist/dialogs/SwapFilesDialog.vue'
import { LOGOUT_ROUTE_NAME, ROOM_DOCUMENTS_ROUTE_NAME, SIGNING_CHECKLIST_ROUTE_NAME, ROOM_ROUTE_TYPES } from '@/router'
import { UPLOAD_HIDDEN_FILE } from '@/store/modules/documents/action_types'
import { FilesActionTypes } from '@/store/modules/files/action_types'
import { GET_ROOM_PROVIDERS } from '@/store/modules/room/action_types'
import { BULK_UPSERT_SIGNING_CHECKLIST_STEP, GET_STEP, REFRESH_SIGNING_CHECKLIST, SWAP_STEP_FILES } from '@/store/modules/signing-checklist/action_types'
import { SET_IMPORTED_FILES, REPLACE_IMPORTED_FILE, SET_SWAP_FILES } from '@/store/modules/signing-checklist/mutation_types'

import { signingChecklistAddInjectionKey, SigningChecklistAddProvidedData } from './inject-keys/signingChecklistAddInjectionKey'
import SigningChecklistAddStep2RecipientWarningDialog from '../signing-checklist/add/step2/SigningChecklistAddStep2RecipientWarningDialog.vue'
import SigningChecklistAddStep2SignatoryWarningDialog from '../signing-checklist/add/step2/SigningChecklistAddStep2SignatoryWarningDialog.vue'
import SigningChecklistAddStep3 from '../signing-checklist/add/step3/SigningChecklistAddStep3.vue'

const { mapActions: mapDocumentsActions } = createNamespacedHelpers('documents')
const {
  mapActions: mapSigningChecklistActions,
  mapMutations: mapSigningChecklistMutations,
  mapState: mapSigningChecklistState,
  mapGetters: mapSigningChecklistGetters,
} = createNamespacedHelpers('signingChecklist')
const { mapState: mapUserState } = createNamespacedHelpers('user')
const { mapActions: mapFilesActions } = createNamespacedHelpers('files')
const { mapState: mapRoomState, mapActions: mapRoomActions } = createNamespacedHelpers('room')
const { mapActions: mapGroupsActions } = createNamespacedHelpers('groups')

type DataType = {
  actionLoading: boolean
  cannotAdd: boolean
  creationLoading: boolean
  envelopeToPrepareIndex: number
  hasPassedStep3: boolean
  leaveLocation: RouteLocationNamedRaw | null
  showBeforeLeaveDialog: boolean
  showCancelDialog: boolean
  showStep2MissingRecipientWarningDialog: boolean
  showStep2RecipientWarningDialog: boolean
  showStep2SignatoryWarningDialog: boolean
  showTagsOverlapsDialog: boolean
  stepActive: number
}
type SwapFileData = {
  envelopeId?: number
  fileId?: number
  retainTags?: boolean
  originalFileId?: number
  newFileToSwap?: object
}
export type SignerWitnesses = ObjectWith<{
  signer: any
  signerId?: number
  witness: Array<any>
  witnessOfSignerId?: number
}>
export type ObjectToPost = ObjectWith<{
  date: string | null
  envelope?: ObjectWith<{
    id: number
    file?: ObjectWith<{
      nbPages?: number
    }>
    observers: Array<any>
    parentMultienvelopeId?: number
    status: ENVELOPE_STATUS
    validators: Array<any>
  }>
  envelopeType: ENVELOPE_TYPES
  fileId: number
  fileName?: string
  fileRaw?: any
  observers: Array<any>
  prevId?: number
  providerOptions: Record<string, boolean>
  recipients: Array<any>
  signatureType: string
  signers: Array<any>
  template?: ObjectWith<{
    id: number
    signers: Array<ObjectWith<{
      templateSignerId: number
      assignedSignerId: number
    }>>
  }>
  uid?: number
  uniqueSigners: Array<any>
  validators: Array<any>
  witnesses?: Array<SignerWitnesses>
}>

export type GenericSignature = {
  id: number
  signerName : string
  assignedSigner: EnvelopeSigner | null
  signerType: SIGNATURE_TYPES
}

export type SigningChecklistFile = ObjectWith<{
  envelope?: ObjectWith<{
    status: ENVELOPE_STATUS
  }>
  id: number
  name?: string
  nbPages?: number
  objectToPost: ObjectToPost
  originalFile: ObjectWith<{
    id: number
    nbPages?: number
  }>
  progress?: number
  providerOptions: Record<string, boolean>
  signedOnClosd?: boolean
  size: number
  status: string
  templateId: number
  templateName?: string
  templateSignatures: Array<GenericSignature>
}>

export default defineComponent({
  name: 'SigningChecklistAdd',
  components: {
    SigningChecklistAddBeforeLeaveDialog,
    SigningChecklistAddStep2MissingRecipientWarningDialog,
    SigningChecklistAddStep2RecipientWarningDialog,
    SigningChecklistAddStep2SignatoryWarningDialog,
    SigningChecklistAddStep3,
    SigningChecklistPrepareCancelDialog,
    SigningChecklistTagsOverlapsDialog,
    AppButton,
    SigningChecklistAddStep1,
    SigningChecklistAddStep2,
    SwapFilesDialog,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    files: {
      type: Array as PropType<Array<SigningChecklistFile>>,
      default: () => [],
    },
    steps: {
      type: Array as PropType<Array<SigningChecklistFile>>,
      default: () => [],
    },
  },
  setup () {
    const route = useRoute()
    const providedData: SigningChecklistAddProvidedData = {
      addFromTemplate: !!route.query['envelopeTemplatesIds'],
    }
    provide(signingChecklistAddInjectionKey, providedData)

    return {
      v$: useVuelidate(),
    }
  },
  data (): DataType {
    return {
      actionLoading: false, // Loading that shows after an action by the user
      cannotAdd: false,
      creationLoading: true, // Blocking loading only done on created lifecycle
      envelopeToPrepareIndex: 0,
      hasPassedStep3: false,
      leaveLocation: null,
      showBeforeLeaveDialog: false,
      showCancelDialog: false,
      showStep2MissingRecipientWarningDialog: false,
      showStep2RecipientWarningDialog: false,
      showStep2SignatoryWarningDialog: false,
      showTagsOverlapsDialog: false,
      stepActive: 1,
    }
  },
  computed: {
    ...mapUserState(['profile']),
    ...mapSigningChecklistState(['importedFiles']),
    ...mapSigningChecklistGetters(['importedFilesInDraft']),
    ...mapRoomState(['currentRoom', 'roomProvidersPending']),
    ...mapState('prepareSignature', ['lastSave', 'tagsOverlaps']),
    ...mapState('room', ['currentRoom']),
    ...mapState('documents', ['uploadHiddenFilePendingIds']),
    ...mapState('signingChecklist', ['swapFiles']),
    ...mapGetters('room', ['isCurrentUserPm']),
    addFromTemplate () {
      return !!this.$route.query['envelopeTemplatesIds']
    },
    dayjs () {
      return dayjs
    },
    anyImportedFiles () {
      return this.importedFiles.length > 0
    },
    queuedFilesInProgress () {
      return this.uploadHiddenFilePendingIds.length
    },
    filesToUpload () {
      return this.importedFiles.filter((f: SigningChecklistFile) => f.objectToPost.hasOwnProperty('fileRaw') && !f.objectToPost.fileId)
    },
    allFilesToUploadSuccess () {
      return this.filesToUpload.every((f: SigningChecklistFile) => f.status === 'success')
    },
    draftedDocuments () {
      return this.importedFilesInDraft
    },
    stepsFromState () {
      return window.history.state.steps || []
    },
    atLeastOneRecipientForEachFile () {
      return this.importedFiles.every((file: SigningChecklistFile) => file.objectToPost.recipients.length > 0)
    },
    isStep3Disabled () {
      return this.stepsFromState.length && this.stepsFromState.every((step: SigningChecklistFile) => step.envelope?.status === ENVELOPE_STATUS.SIGNED)
    },
    hasEverySingleEnvelopeSignatoriesInEveryFiles () {
      const singleFiles = this.importedFiles.filter((file: SigningChecklistFile) => file.objectToPost.envelopeType === ENVELOPE_TYPES.SINGLE)
      return singleFiles.every((file: SigningChecklistFile) => {
        return !file.objectToPost.signers.every(signersGroup => signersGroup.length === 0)
      })
    },
    hasEveryMultipleEnvelopeTwoUniqueSigners () {
      const multipleFiles = this.importedFiles.filter((file: SigningChecklistFile)  => file.objectToPost.envelopeType === ENVELOPE_TYPES.MULTIPLE)
      return multipleFiles.every((file: SigningChecklistFile)  => {
        return file.objectToPost.uniqueSigners.length >= 2
      })
    },
    currentStepActiveClass () {
      const baseActiveClass = 'stepper-step-'
      let stepActiveClassFragment = ''
      if (this.stepActive === 1) {
        stepActiveClassFragment = 'files'
      } else if (this.stepActive === 2) {
        stepActiveClassFragment = 'settings'
      } else if (this.stepActive === 3) {
        stepActiveClassFragment = 'tags'
      }
      return baseActiveClass + stepActiveClassFragment
    },
    hasDocumentsWithTagsOverlaps () {
       return this.importedFilesInDraft.some(
        (file: SigningChecklistFile) => this.tagsOverlaps.find(
          (item: ObjectWith<{ envelopeId: number, tagIds: Array<number> }>) => item.envelopeId === file.objectToPost.envelope?.id && item.tagIds,
        ),
      )
    },
    hasOnlyReadOnlyFiles () {
      return this.importedFiles.length && this.importedFiles.every((file: SigningChecklistFile) => canShowReadOnlyStep(file.objectToPost))
    },
  },
  async created () {
    if (this.currentRoom.isDataroom) {
      this.$router.replace({ name: ROOM_DOCUMENTS_ROUTE_NAME, params: { roomType: ROOM_ROUTE_TYPES.DATAROOM } })
    }
    if (this.isCurrentUserPm && !this.roomProvidersPending && !this.currentRoom.providers) {
      this.GET_ROOM_PROVIDERS(this.mnemo)
    }
    if (!this.actionLoading) {
      // We await because it's used afterwards for formatting the files
      await this.GET_GROUPS(this.mnemo)
    }

    try {
      if (this.$route.query['envelopeTemplatesIds']) {
        await this.prepareEnvelopeTemplatesAsFormattedSteps()
      } else {
        const { query } = this.$route
        const stepPromises = []
        const filePromises = []
        const {
          files: filesFromState = [],
        } = window.history.state

        let newFiles = filesFromState
        let newSteps = this.stepsFromState

        const queryStepIds = query['stepIds'] as string
        const queryFileIds = query['fileIds'] as string

        // If we navigated here through the application's runtime,
        // the steps are passed with their information already through the router
        // Otherwise, we only have the step ids in the URL left and have to fetch
        // their information.
        if (this.stepsFromState.length === 0 && queryStepIds) {
          const stepIdsArray = queryStepIds.split(',')
          for (const stepId of stepIdsArray) {
            stepPromises.push(this.GET_STEP({
              mnemo: this.mnemo,
              id: stepId,
            }))
          }
          const promisedSteps = await Promise.allSettled(stepPromises)
          newFiles = []
          promisedSteps.forEach((promisedStep) => {
            if (promisedStep.status === 'fulfilled') {
              newSteps.push(promisedStep.value)
            }
          })
        } else if (filesFromState.length === 0 && queryFileIds) {
          const fileIdsArray = queryFileIds.split(',')
          for (const fileId of fileIdsArray) {
            filePromises.push(this.GET_FILE({
              mnemo: this.mnemo,
              id: fileId,
            }))
          }
          const promisedFiles = await Promise.allSettled(filePromises)
          newFiles = []
          promisedFiles.forEach((promisedFile) => {
            if (promisedFile.status === 'fulfilled') {
              newFiles.push(promisedFile.value)
            }
          })
        }

        newSteps = newSteps.map((step: Array<SigningChecklistFile>) => ((formatStep(step) as unknown) as SigningChecklistFile))
        newFiles = newFiles.map((file: Array<SigningChecklistFile>) => ((formatImportedFile(file) as unknown) as SigningChecklistFile))

        this.SET_IMPORTED_FILES(newSteps.concat(newFiles))
      }
    } catch (e) {
      console.error(e)
    } finally {
      const { query } = this.$route
      if (query['step']) {
        const queryStep = Number(query['step'])
        if (queryStep >= 0 && queryStep < 4) {
          if (queryStep > 1) {
            this.cannotAdd = true
          }
          this.stepActive = queryStep
        }
      }
      this.creationLoading = false
    }
  },
  beforeUnmount () {
    this.SET_IMPORTED_FILES([])
  },
  beforeRouteLeave (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    const isNavigationToLogout = to.name === LOGOUT_ROUTE_NAME
    if (isNavigationToLogout) {
      next()
    }

    if (this.stepActive === 2 && this.leaveLocation === null && to.name) {
      this.leaveLocation = { name: to.name }

      if (!this.hasOnlyReadOnlyFiles) {
        this.showBeforeLeaveDialog = true
      }

      next(false)
    } else {
      next()
    }
  },
  methods: {
    ...mapDocumentsActions([UPLOAD_HIDDEN_FILE]),
    ...mapSigningChecklistActions([BULK_UPSERT_SIGNING_CHECKLIST_STEP, GET_STEP, REFRESH_SIGNING_CHECKLIST, SWAP_STEP_FILES]),
    ...mapSigningChecklistMutations([SET_IMPORTED_FILES, REPLACE_IMPORTED_FILE, SET_SWAP_FILES]),
    ...mapFilesActions([FilesActionTypes.GET_FILE]),
    ...mapRoomActions([GET_ROOM_PROVIDERS]),
    ...mapGroupsActions(['GET_GROUPS']),
    ...mapActions('envelopeTemplates', ['GET_ENVELOPE_TEMPLATE']),
    async checkStepValidation () {
      if (this.stepActive === 1) {
        if (this.anyImportedFiles) {
          this.stepActive++
        }
      } else if (this.stepActive === 2) {
        this.v$.$touch()
        if (!this.v$.$invalid) {
          if (!this.atLeastOneRecipientForEachFile) {
            this.showStep2MissingRecipientWarningDialog = true
            return
          }

          if (!this.isCurrentUserRecipientInEveryFiles()) {
            this.showStep2RecipientWarningDialog = true
            return
          }

          if (!this.hasEverySingleEnvelopeSignatoriesInEveryFiles || !this.hasEveryMultipleEnvelopeTwoUniqueSigners) {
            this.showStep2SignatoryWarningDialog = true
            return
          }

          await this.upsertAllImportedFilesToSteps()
          if (!this.isStep3Disabled) {
            this.stepActive++
            this.hasPassedStep3 = true
          } else {
            this.returnToSigningChecklist()
          }
        } else {
          toast.warning(this.$t('common.validations.fillOutAll'))
        }
      }
    },
    async confirmWarningStep2Dialog () {
      await this.upsertAllImportedFilesToSteps()
      this.stepActive++
    },
    async upsertAllImportedFilesToSteps () {
      this.actionLoading = true
      const importedFilesBeforeUpdate = cloneDeep(this.importedFiles)
      const objectsToPost: Array<ObjectToPost> = []
      this.importedFiles.forEach((file: SigningChecklistFile, index: number) => {
        if (file.objectToPost.signatureType === SIGNATURE_TYPE_HANDWRITTEN) {
          file.objectToPost.providerOptions['mercury'] = file.providerOptions['mercury']  || false
        }
        file.objectToPost.uid = index
        file.status = 'pending'
        // If the file is swapped without prevId, we set it to -1, avoid error from the backend
        if (file.objectToPost.prevId === undefined) {
          file.objectToPost.prevId = -1
        }
        if (file.objectToPost.witnesses?.flatMap(w => w.witness)?.includes(undefined)) {
          const signerWitnesses: Array<SignerWitnesses> = []
          file.objectToPost.signers.flat().forEach(signer => {
            const witnesses = file.objectToPost.witnesses?.filter(w => w.witnessOfSignerId === signer).map(w => w.signerId)
            if (witnesses && witnesses.length) {
              signerWitnesses.push({ signer: signer, witness: witnesses })
            }
          })
          file.objectToPost.witnesses = signerWitnesses
        }

        if (this.addFromTemplate) {
          file.objectToPost.template = {
            id: file.templateId,
            signers: file.templateSignatures
              .filter(signature => signature.signerType === SIGNATURE_TYPES.SINGLE && signature.assignedSigner)
              .map(signature => ({
                templateSignerId: signature.id,
                assignedSignerId: signature.assignedSigner!.id,
              })),
          }
        }

        objectsToPost.push(file.objectToPost)
      })
      if (objectsToPost.length !== 0) {
        try {
          const data = await this.BULK_UPSERT_SIGNING_CHECKLIST_STEP({
            mnemo: this.mnemo,
            steps: objectsToPost,
            })

          for (const backendStep of data.steps) {
            const frontEndStep = this.importedFiles.find((step: SigningChecklistFile) => step.objectToPost.uid === backendStep.uid)
            frontEndStep.objectToPost = formatStep(backendStep).objectToPost
          }

          if (data.errorSteps.length !== 0) {
            for (const errorUid of data.errorSteps) {
              const frontEndStep = this.importedFiles.find((step: SigningChecklistFile) => step.objectToPost.uid === errorUid)
              frontEndStep.status = 'error'
            }
            return Promise.reject(new Error('Some steps errored'))
          }
          if (data.errorSteps.length === 0 && this.stepsFromState.length > 0) {
            const observersHaveChanged = function (importedFile: SigningChecklistFile) {
              if (importedFile.objectToPost?.observers?.length > 0) {
                if (importedFile.objectToPost?.observers?.length !== importedFile.objectToPost?.envelope?.observers?.length) {
                  return true
                } else {
                  return !importedFile.objectToPost?.observers.every(observer => { return importedFile.objectToPost?.envelope?.observers.some(o => o.id === observer) })
                }
              }
              return false
            }
            const validatorsHaveChanged = function (importedFile: SigningChecklistFile) {
              if (importedFile.objectToPost?.validators?.length > 0) {
                if (importedFile.objectToPost?.validators?.length !== importedFile.objectToPost?.envelope?.validators?.length) {
                  return true
                } else {
                  return !importedFile.objectToPost?.validators.every(validators => { return importedFile.objectToPost?.envelope?.validators.some(v => v.id === validators) })
                }
              }
              return false
            }
            const modifiedObservers = importedFilesBeforeUpdate.some((importedFile: SigningChecklistFile) => observersHaveChanged(importedFile))
            const modifiedValidators = importedFilesBeforeUpdate.some((importedFile: SigningChecklistFile)  => validatorsHaveChanged(importedFile))
            if (modifiedObservers || modifiedValidators) {
              let message
              if (modifiedObservers && modifiedValidators) {
                message = this.$t('project.views.SigningChecklistAdd.addingObserversAndValidatorsSuccess')
              } else if (modifiedObservers) {
                message = this.$t('project.views.SigningChecklistAdd.addingObserversSuccess')
              } else if (modifiedValidators) {
                message = this.$t('project.views.SigningChecklistAdd.addingValidatorsSuccess')
              }
              if (message) {
                toast.success(message)
              }
            }
          }
        } catch (error: any) {
          console.error(error)
          if (error.request?.status === 403) {
            toast.error(error.response?.data?.description)
          } else {
            toast.error(this.$t('common.msgFailErrorOccurred'))
          }
          return Promise.reject(error)
        } finally {
          this.actionLoading = false
        }
      }
    },
    isCurrentUserRecipientInEveryFiles () {
      return this.importedFiles.every((file: SigningChecklistFile) => file.objectToPost.recipients.includes(this.profile.id))
    },
    updateUploadedFiles (files: Array<SigningChecklistFile>) {
      if (files) this.SET_IMPORTED_FILES(files)
      this.launchQueue()
    },
    launchQueue () {
      for (const f of this.filesToUpload) {
        if (f.status === 'queued') {
          if (this.queuedFilesInProgress < (import.meta.env.VUE_APP_SIMULTANEOUS_FILES_UPLOAD || 2)) {
            f.status = 'uploading'
            this.UPLOAD_HIDDEN_FILE({ file: f, mnemo: this.mnemo })
              .then(() => {
                f.objectToPost.fileId = f.id
                f.objectToPost.title = f.basename
              })
              .finally(() => {
                if (this.queuedFilesInProgress < (import.meta.env.VUE_APP_SIMULTANEOUS_FILES_UPLOAD || 2)) {
                  this.launchQueue()
                }
              })
          }
        }
      }
    },
    onCancel () {
      if (this.stepActive > 1) {
        this.showCancelDialog = true
      } else {
        this.returnToSigningChecklist()
      }
    },
    onFinish () {
      if (this.hasDocumentsWithTagsOverlaps && !this.hasOnlyReadOnlyFiles) {
        this.showTagsOverlapsDialog = true
      } else {
        this.returnToSigningChecklist()
      }
    },
    async onSaveAndExit () {
      this.v$.$touch()
      if (!this.v$.$invalid) {
        await this.upsertAllImportedFilesToSteps()
        this.returnToSigningChecklist()
      } else {
        toast.warning(this.$t('common.validations.fillOutAll'))
      }
    },
    returnToSigningChecklist () {
      this.leaveLocation = { name: SIGNING_CHECKLIST_ROUTE_NAME }
      this.$router.push({ name: SIGNING_CHECKLIST_ROUTE_NAME })
    },
    async prepareEnvelopeTemplatesAsFormattedSteps () {
      const envelopeTemplatesPromises = []
      const envelopeTemplatesIds = (this.$route.query['envelopeTemplatesIds'] as string).split(',')
      for (const envelopeTemplateId of envelopeTemplatesIds) {
        envelopeTemplatesPromises.push(this.GET_ENVELOPE_TEMPLATE({ envelopeTemplateId }))
      }
      const promisedEnvelopeTemplates = await Promise.allSettled(envelopeTemplatesPromises)
      const formattedSteps: Array<SigningChecklistFile> = []
      promisedEnvelopeTemplates.forEach(p => {
        if (p.status === 'fulfilled') {
          const stepFromTemplate: SigningChecklistFile = formatStepFromEnvelopeTemplate(p.value.envelopeTemplate)
          formattedSteps.push(stepFromTemplate)
        }
      })
      this.SET_IMPORTED_FILES(formattedSteps)
    },
    resetSwapFiles () {
      this.SET_SWAP_FILES(null)
    },
    async onSwapFileConfirm (swapFileData: SwapFileData[]) {
      if (!swapFileData.length) return

      const firstElementToSwap = swapFileData[0]
      const index = this.importedFiles.findIndex(
        (file: SigningChecklistFile) => file.id === firstElementToSwap.originalFileId,
      )

      // Replace the imported file in the add process
      if (index !== -1) {
        this.REPLACE_IMPORTED_FILE({ index, value: firstElementToSwap.newFileToSwap })
      }

      const isEnvelopeCreated = !!firstElementToSwap.envelopeId
      try {
        if (isEnvelopeCreated) {
          // If the envelope already exists, update the step
          await this.SWAP_STEP_FILES({
            mnemo: this.mnemo,
            data: swapFileData,
          })
          await this.REFRESH_SIGNING_CHECKLIST({
            mnemo: this.mnemo,
          })
        }

        track('swap_success')
        toast.success(this.$t('project.signing-checklist.list.SigningChecklistTable.message.swapFileSuccess'))
      } catch (error) {
        console.error('Error swapping files:', error)
        toast.error(this.$t('project.signing-checklist.list.SigningChecklistTable.message.swapFileError'))
      }
    },
  },
})
</script>

<style scoped lang="scss">
.signing-checklist-title {
  font-size: 1.3rem;
  font-weight: bold;
}
.SigningChecklistAdd-actionsBar {
  align-items: center;
  background-color: rgb(var(--v-theme-grey-25));
  bottom: 0;
  display: flex;
  flex-grow: 0;
  padding: 16px;
  position: sticky;
  width: 100%;
  z-index: 1;
}
.v-stepper-window {
  margin: 0 16px;
}
.v-stepper-header {
  box-shadow: none;
}
.v-window__container .v-stepper-item {
  opacity: 1;
  text-align: left;
}
.v-stepper {
  background-color: rgb(var(--v-theme-grey-25));
}
:deep(.v-stepper-item__content) {
  width: 100%;
  line-height: normal;
}
:deep(.v-window__container .v-avatar.v-stepper-item__avatar) {
  display: none;
}
:deep(.v-stepper--alt-labels .v-stepper-item__avatar.v-avatar) {
  margin-bottom: 8px;
}
</style>
